<template>
  <div
    class="modal fade"
    id="add-network-device"
    tabindex="-1"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-lg modal-dialog-centered modal-simple modal-upgrade-plan"
    >
      <div class="modal-content">
        <div class="modal-body">
          <LoadingElement v-if="loading" />
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            id="add-network-device-close"
          ></button>
          <div class="text-center">
            <h4 class="mb-8">{{ $t("network_devices.add_network_device") }}</h4>
          </div>
          <div class="row g-6">
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.nas_name")
              }}</label>
              <select
                class="form-select"
                @change="validate"
                :class="{ 'is-invalid': errors.nas_id }"
                v-model="network_device.nas_id"
              >
                <option
                  v-for="(nas, index) in nases"
                  :key="nas.value"
                  :value="nas.value"
                  >{{ nas.label }}</option
                >
              </select>
              <div v-if="errors.nas_id" class="invalid-feedback">
                {{ errors.nas_id }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.type")
              }}</label>
              <select
                class="form-select"
                @change="validate"
                v-model="network_device.type"
              >
                <option value="switch">{{
                  $t("network_devices.types.switch")
                }}</option>
                <option value="router">{{
                  $t("network_devices.types.router")
                }}</option>
                <option value="server">{{
                  $t("network_devices.types.server")
                }}</option>
                <option value="ap">{{ $t("network_devices.types.ap") }}</option>
                <option value="dish">{{
                  $t("network_devices.types.dish")
                }}</option>
                <option value="other">{{
                  $t("network_devices.types.other")
                }}</option>
              </select>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.name")
              }}</label>
              <input
                type="text"
                class="form-control"
                v-model="network_device.name"
                @input="validate"
                :class="{ 'is-invalid': errors.name }"
              />
              <div v-if="errors.name" class="invalid-feedback">
                {{ errors.name }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.ip")
              }}</label>
              <input
                type="text"
                class="form-control"
                v-model="network_device.ip_address"
                @input="validate"
                placeholder="x.x.x.x"
                :class="{ 'is-invalid': errors.ip_address }"
              />
              <div v-if="errors.ip_address" class="invalid-feedback">
                {{ errors.ip_address }}
              </div>
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.username_optional")
              }}</label>
              <input
                type="text"
                class="form-control"
                v-model="network_device.username"
                @input="validate"
              />
            </div>
            <div class="col-12 col-md-6">
              <label class="form-label" for="">{{
                $t("network_devices.password_optional")
              }}</label>
              <input
                type="text"
                class="form-control"
                v-model="network_device.password"
                @input="validate"
              />
            </div>
            <div class="col-12">
              <button
                class=" text-blue-400 hover:cursor-pointer underline"
                @click.prevent="edit_location = !edit_location"
              >
                {{ $t("network_devices.set_location_on_map") }}
              </button>
            </div>
            <div class="col-12" v-if="edit_location">
              <l-map
                ref="map"
                style="height: 400px;"
                v-model:zoom="zoom"
                :center="[
                  network_device.latitude ?? 36.185155536944286,
                  network_device.longitude ?? 36.72103984597841,
                ]"
                @click="onMapClick"
              >
                <l-tile-layer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  layer-type="base"
                  name="OpenStreetMap"
                ></l-tile-layer>
                <l-marker
                  v-if="network_device.latitude && network_device.longitude"
                  :lat-lng="[network_device.latitude, network_device.longitude]"
                >
                  <l-icon
                    :icon-url="
                      require(`@/assets/img/map/${network_device.type}.png`)
                    "
                    :icon-size="[32, 32]"
                  ></l-icon>
                </l-marker>
              </l-map>
              <div>
                <div>{{ network_device.latitude }}</div>
                <div>{{ network_device.longitude }}</div>
              </div>
            </div>
            <div
              class="col-12 text-danger"
              v-for="(item, index) in server_errors"
              :key="index"
            >
              {{ item }}
            </div>
            <div class="col-12 d-flex justify-content-end">
              <button
                class="btn btn-success"
                :disabled="!validated"
                @click.prevent="store"
              >
                {{ $t("control.add") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment, { months } from "moment";
import { hasPrivilege } from "../../global_constants.js";
import { mapGetters } from "vuex";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { LMap, LTileLayer, LMarker, LIcon } from "@vue-leaflet/vue-leaflet";
export default {
  name: "NetworkDeviceAdd",
  props: ["visible"],
  computed: {
    ...mapGetters(["user", "settings"]),
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
  },
  data() {
    return {
      loading: true,
      validated: false,
      network_device: {
        name: "",
        ip_address: "",
        username: null,
        password: null,
        nas_id: null,
        latitude: null,
        longitude: null,
        type: "other",
      },
      edit_location: false,
      zoom: 14,
      nases: [],
      server_errors: [],
      switch_icon: require("@/assets/img/map/switch.png"),
      errors: {
        name: null,
        ip_address: null,
        nas_id: null,
      },
    };
  },
  async created() {
    this.loading = false;
    await this.fetchNases();
    if (this.$route.query.network_device_id) {
      this.loading = true;
      const response = await axios.get(
        `nases/devices/${this.$route.query.network_device_id}`,
      );
      if (response.status == 200) {
        this.network_device = response.data.data;
        if (this.network_device.latitude != null) {
          this.edit_location = true;
        }
        this.loading = false;
      }
    }
  },
  methods: {
    hide() {
      this.$emit("hide-details");
    },
    async fetchNases() {
      this.nases = [];
      this.loading = true;
      const response = await axios.get("nases?itr=true&rows=10000000");
      response.data.data.forEach((element) => {
        this.nases.push({ value: element.id, label: element.shortname });
      });
      // if (this.nases.length > 0)
      //   this.network_device.nas_id = this.nases[0].value;
      this.loading = false;
    },
    formatDate(date) {
      return moment(date).format("MMMM Do YYYY, h:mm a");
    },
    hasPrivilege(privilege) {
      return hasPrivilege(this.user, privilege);
    },
    async store() {
      this.loading = true;
      if (!this.network_device.id) {
        const response = await axios.post("nases/devices", this.network_device);
        if (response.status == 200) {
          document.getElementById("add-network-device-close").click();
          this.$notify({
            text: this.$t("network_devices.network_device_added_successfully"),
            type: "success",
          });
          this.$emit("updated");
        } else if (response.status == 210) {
          this.$notify({
            text: this.$t("network_devices.network_device_addition_failed"),
            type: "error",
          });
          this.server_errors = response.data.errors;
          this.validated = false;
        }
      } else {
        const response = await axios.post(
          `nases/devices/${this.network_device.id}`,
          this.network_device,
        );
        if (response.status == 200) {
          document.getElementById("add-network-device-close").click();
          this.$notify({
            text: this.$t("network_devices.network_device_added_successfully"),
            type: "success",
          });
          this.$emit("updated");
        } else if (response.status == 210) {
          this.$notify({
            text: this.$t("network_devices.network_device_addition_failed"),
            type: "error",
          });
          this.server_errors = response.data.errors;
          this.validated = false;
        }
      }
      this.loading = false;
    },
    selectImage() {
      this.channel.logo = this.$refs.imageFile.files.item(0);
      this.currentImageUrl = URL.createObjectURL(this.channel.logo);
    },
    async onMapClick(event) {
      if (!this.edit_location) return;
      const latLng = event.latlng;
      this.network_device.latitude = latLng.lat;
      this.network_device.longitude = latLng.lng;
    },
    validate() {
      this.validated = true;
      this.errors.name = null;
      this.errors.ip_address = null;
      this.errors.nas_id = null;
      if (this.network_device.name == "") {
        this.validated = false;
        this.errors.name = this.$t("errors.network_devices.name_required");
      }
      if (this.network_device.ip_address == "") {
        this.validated = false;
        this.errors.ip_address = this.$t("errors.network_devices.ip_required");
      }
      if (this.network_device.nas_id == null) {
        this.validated = false;
        this.errors.nas_id = this.$t("errors.network_devices.nas_id");
      }
      if (
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
          this.network_device.ip_address,
        ) == false
      ) {
        this.validated = false;
        this.errors.ip_address = this.$t("errors.network_devices.invalid_ip");
      }
    },
  },
};
</script>
